import { Card, Icon } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import Alert from "custom/Alert";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import { useLocation, useHistory } from "react-router-dom";
import { useRef, useState, useMemo, useEffect } from "react";
import FirstPageViewer from "components/PdfViewer";
import { connect } from "react-redux";
import {
  getSoundMaterialByLetter as fetchSoundMaterialByLetter,
  getSoundMaterialTypeLookups as fetchSoundMaterialTypeLookups,
} from "redux/actions/pages/sound-material";
import { SOUND_MATERIAL_TYPE } from "redux/actions/constants";
import PropTypes from "prop-types";

function SelectMaterial({
  getSoundMaterialByLetter,
  getSoundMaterialTypeLookups,
  soundItems,
  lookups,
  localizations,
}) {
  const location = useLocation();
  const history = useHistory();
  const selectedLetters = location.state?.selectedLetters || [];
  const materialName = location.state?.materialName || "";
  const scrollContainerRefs = useRef({});
  const [activeLetter, setActiveLetter] = useState("");
  const [selectedMaterials, setSelectedMaterials] = useState([]);

  // Component mount olduğunda çalışacak useEffect
  useEffect(() => {
    // Ses materyal tiplerini çek
    getSoundMaterialTypeLookups();

    if (selectedLetters && selectedLetters.length > 0) {
      const firstLetter = selectedLetters[0].value;
      setActiveLetter(firstLetter);
      getSoundMaterialByLetter(firstLetter);
    }
  }, []); // Sadece component mount olduğunda çalışsın

  // Update state when location state changes (e.g., when returning from selected-material page)
  useEffect(() => {
    if (location.state?.selectedMaterials) {
      setSelectedMaterials(location.state.selectedMaterials);
    }
    if (location.state?.activeLetter) {
      setActiveLetter(location.state.activeLetter);
    }
  }, [location.state]);

  // Materyalleri soundItems'dan al
  const materials = useMemo(
    () =>
      soundItems.map((item) => ({
        id: item.id,
        letter: item.letter,
        fileUrl: item.file,
        name: item.name,
        type: item.type,
        isActive: item.is_active,
        order: item.sound_order,
      })),
    [soundItems]
  );
  // Group materials by type
  const materialsByType = useMemo(
    () =>
      materials.reduce((acc, material) => {
        const { type } = material;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(material);
        return acc;
      }, {}),
    [materials]
  );

  const materialTypes = useMemo(() => {
    const types = {};

    const typeOptions =
      lookups && lookups[SOUND_MATERIAL_TYPE]
        ? lookups[SOUND_MATERIAL_TYPE].lookupValues.map((lv) => ({
            value: lv.lookupValueKey,
            label:
              localizations &&
              Object.keys(localizations).length > 0 &&
              localizations[lv.lookupValueKey]
                ? localizations[lv.lookupValueKey]
                : lv.lookupValueKey
                    .replace("SOUND_MATERIAL_TYPE_", "")
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
          }))
        : [
            { value: "CLASSROOM", label: "Sınıf İçi Çalışmalar" },
            { value: "ASSESSMENT_TOOLS", label: "Ölçme ve Değerlendirme Araçları" },
            { value: "BOARDS_AND_CARDS", label: "Pano ve Kartlar" },
          ];

    const uniqueTypes = [...new Set(materials.map((m) => m.type))];

    uniqueTypes.forEach((type) => {
      const typeOption = typeOptions.find((option) => option.value === type);
      types[type] = typeOption ? typeOption.label : type;
    });

    return types;
  }, [materials, lookups]);

  const handleScroll = (type, direction) => {
    if (scrollContainerRefs.current[type]) {
      const scrollAmount = 300;
      const newScrollPosition =
        scrollContainerRefs.current[type].scrollLeft +
        (direction === "right" ? scrollAmount : -scrollAmount);
      scrollContainerRefs.current[type].scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
    }
  };

  const handleLetterClick = (letterValue) => {
    setActiveLetter(letterValue);
    getSoundMaterialByLetter(letterValue);
  };

  const handleMaterialClick = (material) => {
    setSelectedMaterials((prev) => {
      const existingIndex = prev.findIndex((m) => m.id === material.id);

      if (existingIndex !== -1) {
        // Materyal zaten seçili, onu kaldır
        const removedItemOrder = prev[existingIndex].selectionOrder;
        const newMaterials = prev
          .filter((m) => m.id !== material.id)
          .map((m) => ({
            ...m,
            selectionOrder:
              m.selectionOrder > removedItemOrder ? m.selectionOrder - 1 : m.selectionOrder,
          }));
        return newMaterials;
      }

      // Yeni materyal ekle
      const newMaterials = [...prev, { ...material, selectionOrder: prev.length + 1 }];
      return newMaterials;
    });
  };

  // Aktif harfe göre materyalleri filtrele
  const filteredMaterialsByType = Object.entries(materialsByType).reduce(
    (acc, [type, materialsOfType]) => {
      const filteredMaterials = materialsOfType.filter(
        (material) => material.letter === activeLetter
      );
      // Sadece içi dolu olan tipleri ekle
      if (filteredMaterials.length > 0) {
        acc[type] = filteredMaterials;
      }
      return acc;
    },
    {}
  );

  // Her harften en az bir seçim yapılıp yapılmadığını kontrol et
  const isAllLettersSelected = useMemo(() => {
    const selectedLetterMaterials = {};
    selectedMaterials.forEach((material) => {
      selectedLetterMaterials[material.letter] = true;
    });
    return selectedLetters.every((letter) => selectedLetterMaterials[letter.value]);
  }, [selectedMaterials, selectedLetters]);

  const handleContinue = () => {
    // Create a string of selected letters
    const selectedLettersString = selectedLetters.map((letter) => letter.label).join(",");

    // Create a string of material types from the selected materials
    const materialTypesSet = new Set();
    selectedMaterials.forEach((material) => {
      materialTypesSet.add(material.type);
    });
    const materialTypesString = Array.from(materialTypesSet).join(",");

    history.push("/sound-materials/create/select-material/selected-materials", {
      selectedMaterials: selectedMaterials.sort((a, b) => a.selectionOrder - b.selectionOrder),
      materialName,
      selectedLettersString,
      materialTypesString,
    });
  };

  const materialTypesOrder = ["CLASSROOM", "ASSESSMENT_TOOLS", "BOARDS_AND_CARDS"];

  return (
    <>
      <Alert />
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox mt={3} mb={4}>
          <Card>
            <SuiBox p={3}>
              {/* Material Name Display */}
              <SuiBox mb={4}>
                <SuiTypography variant="h5" fontWeight="medium">
                  Materyal Seçimi
                </SuiTypography>
              </SuiBox>

              {/* Selected letters display */}
              <SuiBox
                mb={4}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  justifyContent: "flex-end",
                }}
              >
                {selectedLetters.map((letter) => (
                  <SuiBox
                    key={letter.value}
                    onClick={() => handleLetterClick(letter.value)}
                    sx={{
                      width: "35px",
                      height: "35px",
                      borderRadius: "50%",
                      backgroundColor:
                        activeLetter === letter.value ? "rgb(52, 71, 103)" : "rgb(200, 200, 200)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor:
                          activeLetter === letter.value ? "rgb(42, 61, 93)" : "rgb(180, 180, 180)",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <SuiTypography
                      variant="button"
                      sx={{
                        color: "rgb(255, 255, 255)",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    >
                      {letter.label}
                    </SuiTypography>
                  </SuiBox>
                ))}
              </SuiBox>
              {/* Material type sections */}
              {Object.entries(materialTypes)
                .filter(([type]) => type in filteredMaterialsByType) // Sadece içi dolu olan tipleri göster
                .sort((a, b) => materialTypesOrder.indexOf(a[0]) - materialTypesOrder.indexOf(b[0]))
                .map(([type, label]) => (
                  <SuiBox key={type} mb={3}>
                    <SuiTypography variant="h6" fontWeight="medium" mb={2}>
                      {label}
                    </SuiTypography>
                    <SuiBox
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <SuiButton
                        variant="outlined"
                        color="dark"
                        circular
                        iconOnly
                        onClick={() => handleScroll(type, "left")}
                        sx={{
                          position: "absolute",
                          left: -20,
                          zIndex: 2,
                          minWidth: "40px",
                          width: "40px",
                          height: "40px",
                          boxShadow: "lg",
                        }}
                      >
                        <Icon>chevron_left</Icon>
                      </SuiButton>

                      <SuiBox
                        ref={(el) => {
                          scrollContainerRefs.current[type] = el;
                        }}
                        sx={{
                          display: "flex",
                          gap: 2,
                          overflowX: "auto",
                          scrollBehavior: "smooth",
                          "&::-webkit-scrollbar": { display: "none" },
                          msOverflowStyle: "none",
                          scrollbarWidth: "none",
                          padding: "1rem",
                        }}
                      >
                        {(filteredMaterialsByType[type] || []).map((material) => {
                          const isSelected = selectedMaterials.some((m) => m.id === material.id);
                          const selectionOrder = isSelected
                            ? selectedMaterials.find((m) => m.id === material.id).selectionOrder
                            : null;

                          return (
                            <SuiBox
                              key={material.id}
                              onClick={() => handleMaterialClick(material)}
                              sx={{
                                width: "198px",
                                height: "280px",
                                backgroundColor: "white",
                                borderRadius: "lg",
                                boxShadow: "sm",
                                border: "1px solid",
                                borderColor: "grey.300",
                                flexShrink: 0,
                                cursor: "pointer",
                                transition: "transform 0.2s",
                                position: "relative",
                                "&:hover": {
                                  transform: "translateY(-4px)",
                                },
                                display: "flex",
                                flexDirection: "column",
                                padding: 2,
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <SuiBox
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  height: "100%",
                                  ...(isSelected && {
                                    "&::after": {
                                      content: '""',
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                                      borderRadius: "lg",
                                      zIndex: 2,
                                    },
                                  }),
                                }}
                              >
                                <FirstPageViewer fileUrl={material.fileUrl} />
                                {isSelected && (
                                  <SuiBox
                                    sx={{
                                      position: "absolute",
                                      top: "10px",
                                      right: "10px",
                                      width: "30px",
                                      height: "30px",
                                      borderRadius: "50%",
                                      backgroundColor: "rgb(52, 71, 103)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      zIndex: 3,
                                    }}
                                  >
                                    <SuiTypography
                                      variant="button"
                                      sx={{
                                        color: "rgb(255, 255, 255)",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {selectionOrder}
                                    </SuiTypography>
                                  </SuiBox>
                                )}
                              </SuiBox>
                              <SuiTypography
                                variant="button"
                                sx={{
                                  mt: 1,
                                  color: "text",
                                  fontWeight: "medium",
                                  textAlign: "center",
                                }}
                              >
                                {material.name}
                              </SuiTypography>
                            </SuiBox>
                          );
                        })}
                      </SuiBox>

                      <SuiButton
                        variant="outlined"
                        color="dark"
                        circular
                        iconOnly
                        onClick={() => handleScroll(type, "right")}
                        sx={{
                          position: "absolute",
                          right: -20,
                          zIndex: 2,
                          minWidth: "40px",
                          width: "40px",
                          height: "40px",
                          boxShadow: "lg",
                        }}
                      >
                        <Icon>chevron_right</Icon>
                      </SuiButton>
                    </SuiBox>
                  </SuiBox>
                ))}
              {/* Continue Button */}
              <SuiBox mt={4} display="flex" justifyContent="flex-end">
                <SuiButton
                  variant="gradient"
                  color="dark"
                  onClick={handleContinue}
                  disabled={!isAllLettersSelected}
                >
                  {isAllLettersSelected
                    ? "Devam Et"
                    : `Lütfen her harften en az bir materyal seçin (${
                        selectedLetters.length -
                        Object.keys(
                          selectedMaterials.reduce(
                            (acc, mat) => ({ ...acc, [mat.letter]: true }),
                            {}
                          )
                        ).length
                      } harf kaldı)`}
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </Card>
        </SuiBox>
      </DashboardLayout>
    </>
  );
}

SelectMaterial.propTypes = {
  getSoundMaterialByLetter: PropTypes.func.isRequired,
  getSoundMaterialTypeLookups: PropTypes.func.isRequired,
  soundItems: PropTypes.arrayOf(
    PropTypes.shape({
      letter: PropTypes.string,
      file: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      is_active: PropTypes.bool,
      sound_order: PropTypes.number,
    })
  ),
  lookups: PropTypes.shape({}),
  localizations: PropTypes.objectOf(PropTypes.any),
};

SelectMaterial.defaultProps = {
  soundItems: [],
  lookups: {},
  localizations: null,
};

const mapStateToProps = (state) => ({
  soundItems: state.data.soundItems || [],
  lookups: state.data.lookups || {},
  localizations: state.localization?.pairs,
});

const mapDispatchToProps = {
  getSoundMaterialByLetter: fetchSoundMaterialByLetter,
  getSoundMaterialTypeLookups: fetchSoundMaterialTypeLookups,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectMaterial);
